import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { useIsNativeEmbed } from '../../../music24/navigation/hooks/useIsNativeEmbed';
import { formatCatalogDay } from '../catalogDates';
import { formatPortalFreshness } from '../artistPortalCardState';
import { catalogServiceName } from '../earlyAccess/earlyAccessCopy';
import {
  isDittoCaptureCard,
  isSignInOnlyCaptureCard,
  numbersStopDay,
  SessionCaptureCardState,
} from './sessionCaptureCardState';
import { captureMessages, sessionCaptureMessages as messages } from './sessionCaptureMessages';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import portalStyles from '../ConnectionCardPortal.module.scss';
import catalogStyles from '../Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SessionCaptureStatusLinesProps {
  connection: ServiceConnection;
  state: SessionCaptureCardState;
  now?: number;
}

/** True when an ISO datetime is on the same UTC day as `now`. */
function isSameUtcDay(iso: string, now: number): boolean {
  return new Date(iso).toISOString().slice(0, 10) === new Date(now).toISOString().slice(0, 10);
}

/**
 * The note lines of a card whose sign-in runs in the app, one set for each state (design
 * your-intelligence k1). Ditto Music keeps its own copy; every other service gets the general copy.
 */
const SessionCaptureStatusLinesComponent: React.FC<SessionCaptureStatusLinesProps> = ({
  connection,
  state,
  now: fixedNow,
}) => {
  const intl = useIntl();
  const isNativeEmbed = useIsNativeEmbed();
  const now = fixedNow ?? Date.now();
  const isDitto = isDittoCaptureCard(connection);
  const service = catalogServiceName(intl, connection.integrationType);

  const freshness = useMemo(() => {
    const { lastSyncAt, syncedThrough } = connection;
    if (lastSyncAt !== null && syncedThrough !== null && isSameUtcDay(lastSyncAt, now)) {
      return intl.formatMessage(messages.syncedToday, { day: formatCatalogDay(intl, syncedThrough) });
    }
    return formatPortalFreshness(intl, connection, now);
  }, [connection, intl, now]);

  if (state === SessionCaptureCardState.notConnected) {
    return (
      <>
        <div className={catalogStyles.connectionNote}>
          {isDitto
            ? intl.formatMessage(messages.note)
            : withBrandWord(intl.formatMessage(captureMessages.note, { service }))}
        </div>
        {isNativeEmbed && (
          <div className={portalStyles.connectionSoftNote}>
            {isDitto
              ? intl.formatMessage(messages.nativeLoginNote)
              : withBrandWord(intl.formatMessage(captureMessages.nativeLoginNote, { service }))}
          </div>
        )}
      </>
    );
  }
  if (connection.signInNotice === 'noArtistAccess') {
    return (
      <div className={portalStyles.connectionSoftNote}>
        {intl.formatMessage(captureMessages.noArtistAccess, { service })}
      </div>
    );
  }
  // A sign-in-only service reads no number yet: its card stays on this line while it is connected.
  if (isSignInOnlyCaptureCard(connection) && state !== SessionCaptureCardState.reconnect) {
    return (
      <div className={portalStyles.connectionSoftNote}>{intl.formatMessage(captureMessages.signInOnlyFirstRead)}</div>
    );
  }
  if (state === SessionCaptureCardState.firstRead) {
    return <div className={portalStyles.connectionSoftNote}>{intl.formatMessage(messages.firstRead)}</div>;
  }
  if (state === SessionCaptureCardState.needsArtist) {
    return <div className={portalStyles.connectionSoftNote}>{intl.formatMessage(messages.noArtist)}</div>;
  }
  if (state === SessionCaptureCardState.reconnect) {
    const stop = numbersStopDay(connection);
    return (
      <div className={portalStyles.connectionAlertNote}>
        {withBrandWord(
          intl.formatMessage(isDitto ? messages.signedOutNote : captureMessages.signedOutNote, {
            date: stop === null ? '—' : formatCatalogDay(intl, stop),
            service,
          })
        )}
      </div>
    );
  }
  return (
    <>
      <div className={catalogStyles.connectionNote}>
        {intl.formatMessage(isDitto ? messages.inStreaming : captureMessages.inStreaming)}
      </div>
      {freshness !== undefined && <div className={portalStyles.connectionSoftNote}>{freshness}</div>}
    </>
  );
};

export const SessionCaptureStatusLines = TypedMemo(SessionCaptureStatusLinesComponent);
